import { RootState } from 'redux/store';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReactComponent as PlusIcon } from '../../../../../../assets/icons/phosphor-plus.svg';
import { ReactComponent as ClipboardIcon } from '../../../../../../assets/icons/phosphor-clipboard-text.svg';
import { ReactComponent as HandshakeIcon } from '../../../../../../assets/icons/phosphor-handshake.svg';
import * as S from '../../../BACKOFFICEFLOW/MYCLIENTS/components/dashboard/salesOnboarding/index.styled';
import * as DashS from '../../../../Dashboard.styled';
import InitialDocuments from '../../../BACKOFFICEFLOW/CLIENTVIEW/components/ClientProfile/components/InitialDocuments/InitialDocuments';

/**
 * Pathname matcher for the Visa Assessment and Retainer Agreement screens
 * inside the sales-onboarding flow. Captures the candidate id from the URL
 * so the right-rail Initial Documents card can be scoped to whichever client
 * the onboarder has selected via SelectClientSearchBar (which navigates to
 * `/dashboard/sales-onboarding/{assessment|agreement}/:id`).
 *
 * The id is optional in the App.tsx route definition (`assessment/:id?`),
 * so the regex captures it as an optional group — when no client is selected
 * yet, `candidateID` resolves to `null` and the card hides itself.
 */
const ASSESSMENT_OR_AGREEMENT_RE = /\/sales-onboarding\/(assessment|agreement)(?:\/(\d+))?(?:\/|$)/;

export default function OnboardingButtons({ closeMenu }: { closeMenu?: () => void }) {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const location = useLocation();

    // Parse the pathname to figure out (a) whether the user is currently on
    // the Visa Assessment or Retainer Agreement screen, and (b) which client
    // is being onboarded. We rely on the URL rather than redux because the
    // route param is the source of truth — `salesClients.currentClient` is
    // a derived mirror that lags by one render during navigation.
    const match = location.pathname.match(ASSESSMENT_OR_AGREEMENT_RE);
    const onAssessmentOrAgreement = Boolean(match);
    const candidateIdFromUrl = match?.[2] ?? null;

    // Redux fallback covers cases where the URL hasn't yet been updated but
    // a client has already been selected (e.g. the route param is omitted in
    // the menu link, but the store still holds the last-selected client).
    const currentClientId = useSelector(
        (state: RootState) => state.salesClients.currentClient?.candidate_id ?? null
    );
    const candidateID = candidateIdFromUrl ?? (currentClientId ? String(currentClientId) : null);

    const showInitialDocuments = onAssessmentOrAgreement && Boolean(candidateID);

    return (
        <DashS.CardStack>
            <S.OnboardingContainer>
                <S.OnboardingTitle>{t('onboarding', { lng: language })}</S.OnboardingTitle>
                <S.OnboardingContent>
                    <S.OnboardingRoundWrapper>
                        <S.OnboardingRoundContainers>
                            <PlusIcon fill="white" style={{ width: '28px', height: '28px' }} />
                        </S.OnboardingRoundContainers>
                        <S.OnboardingLine></S.OnboardingLine>
                        <S.OnboardingRoundContainers>
                            <ClipboardIcon fill="white" style={{ width: '28px', height: '28px' }} />
                        </S.OnboardingRoundContainers>
                        <S.OnboardingLine></S.OnboardingLine>
                        <S.OnboardingRoundContainers>
                            <HandshakeIcon fill="white" style={{ width: '28px', height: '28px' }} />
                        </S.OnboardingRoundContainers>
                    </S.OnboardingRoundWrapper>
                    <S.OnboardingButtonWrapper>
                        <S.StyledActiveLink onClick={closeMenu} to="sales-onboarding" end>
                            {t('clientOnboarding', { lng: language })}
                        </S.StyledActiveLink>
                        <S.StyledActiveLink onClick={closeMenu} to="sales-onboarding/assessment">
                            {t('visaAssessment', { lng: language })}
                        </S.StyledActiveLink>
                        <S.StyledActiveLink onClick={closeMenu} to="sales-onboarding/agreement">
                            {t('retainerAgreement', { lng: language })}
                        </S.StyledActiveLink>
                    </S.OnboardingButtonWrapper>
                </S.OnboardingContent>
            </S.OnboardingContainer>
            {/* Initial Documents card is only relevant on the Visa Assessment
                or Retainer Agreement screens, *and* only once a client has
                been selected (via SelectClientSearchBar). It's hidden on the
                bare "Client Onboarding" landing page where no candidate
                context exists yet. The candidate id is forwarded explicitly
                via the `userId` prop so the card scopes its document fetches
                to the client picked in the search bar / URL, rather than
                falling back to whatever happens to be in `activeApp`. */}
            {showInitialDocuments && <InitialDocuments userId={candidateID} />}
        </DashS.CardStack>
    );
}
